import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SubscriptionService from "@/services/api/subscription";
import type { SubscribedUser, GetSubscriptionResponse, GetSubscriptionParams } from "@/types/subscription";

interface SubscriptionState {
  currentSubscription: SubscribedUser | null;
  subscriptionHistory: SubscribedUser[];
  isLoadingCurrent: boolean;
  isLoadingHistory: boolean;
  error: string | null;
  historyError: string | null;
}

const initialState: SubscriptionState = {
  currentSubscription: null,
  subscriptionHistory: [],
  isLoadingCurrent: false,
  isLoadingHistory: false,
  error: null,
  historyError: null,
};

export const fetchUserSubscription = createAsyncThunk(
  "subscription/fetchUserSubscription",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getUserSubscription();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load subscription");
    }
  },
);

export const fetchSubscriptionHistory = createAsyncThunk(
  "subscription/fetchSubscriptionHistory",
  async (params: GetSubscriptionParams = {}, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getSubscriptionHistory(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load subscription history");
    }
  },
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearSubscription: (state) => {
      state.currentSubscription = null;
      state.subscriptionHistory = [];
      state.error = null;
      state.historyError = null;
      state.isLoadingCurrent = false;
      state.isLoadingHistory = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserSubscription.pending, (state) => {
        state.isLoadingCurrent = true;
        state.error = null;
      })
      .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        state.isLoadingCurrent = false;
        state.currentSubscription = action.payload;
        state.error = null;
      })
      .addCase(fetchUserSubscription.rejected, (state, action) => {
        state.isLoadingCurrent = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSubscriptionHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.historyError = null;
      })
      .addCase(fetchSubscriptionHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        const historyData = action.payload as GetSubscriptionResponse<SubscribedUser>;
        state.subscriptionHistory = historyData.results || [];
        state.historyError = null;
      })
      .addCase(fetchSubscriptionHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.historyError = action.payload as string;
      });
  },
});

export const { clearSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
